// 3rd-party modules
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

// project modules
import Select from '../shared/inputs/select';
import Popup from '../shared/popup/popup';
import Loader from '../shared/loader';
import Button from '../shared/button';
import { apiCall } from '../../helpers/apiHelper';
import { RootState } from '../../stores';
import { setKiboshDevice } from '../../stores/userStore';
import { useAppDispatch } from '../../hooks/storeHooks';

// apis
import * as AccountKiboshDeviceApi from '../../apis/accountKiboshDeviceApi';

// models
import { Account } from '../../models/account';
import { AccountKiboshDevice } from '../../models/accountKiboshDevice';
import { DataSourceRequest } from '../../models/dataSourceRequest';

type Props = {
  open: boolean;
  account: Account;
  modalTitle?: string;
  onClose?: () => void;
  onSave?: () => void;
};

export default function KiboshDevicePickerModal({ open, account, modalTitle = "", onClose, onSave }: Props) {
  const kiboshDevice = useSelector((state: RootState) => state.user.kiboshDevice) || new AccountKiboshDevice();

  const [accountKiboshDevices, setAccountKiboshDevices] = useState<AccountKiboshDevice[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentKiboshDeviceReference, setCurrentKiboshDeviceReference] = useState<string>(kiboshDevice.kiboshDeviceReference || "");

  const dispatch = useAppDispatch();
  const abortController = new AbortController();

  useEffect(() => {
    if (account.accountId) {
      getAccountKiboshDevicesAsync()
    }
  }, [account])

  const onCancel = () => {
    abortController.abort();

    if(onClose) onClose();
  };

  const onSubmit= async () => {
    const data = accountKiboshDevices.find(x => x.kiboshDeviceReference === currentKiboshDeviceReference);
    dispatch(setKiboshDevice(data || new AccountKiboshDevice()));

    if (onSave) {
      onSave()
    }
    window.location.reload();
  };

  const getAccountKiboshDevicesAsync = async () => {
    setAccountKiboshDevices(await getAccountKiboshDevices({}, abortController.signal));
  }

  const getAccountKiboshDevices = async (request?: DataSourceRequest, abortSignal?: AbortSignal) => {
    setLoading(true);
    const response = await apiCall(AccountKiboshDeviceApi.getAccountKiboshDevices(account?.accountId!, request, abortSignal));
    setLoading(false);

    const data = response.success ? AccountKiboshDevice.toArrayOfClass(response.data?.value || []) : [];

    const filteredDevices = data.filter(x => (x.hasActiveSubscription || x.kiboshDeviceTypeCode?.toLowerCase() == "router"  ) && x.kiboshDeviceTypeCode?.toLowerCase() != "vpn"); //don't show vpns in device picker
    const dnsDevices = data.filter(x => x.kiboshDeviceTypeCode?.toLowerCase() === "dns" && !!x.lastSubscriptionId); // to avoid showing newly added dns

    if (!filteredDevices.length && dnsDevices.length) { // Show DNS devices even if all of them doesn't have active subscription
      const dnsSubIds = dnsDevices.map(x => x.lastSubscriptionId);
      const dnsRelatedVPNs = data.filter(x => x.kiboshDeviceTypeCode?.toLowerCase() === "vpn" && dnsSubIds.includes(x.lastSubscriptionId)); // to show VPNs that are a part of KiboshLight

      return [...dnsDevices, ...dnsRelatedVPNs];
    }

    return filteredDevices;
  };

  return (
    <Popup
      title={ modalTitle || "Select Kibosh device"}
      onCancel={onCancel}
      onClose={onCancel}
      onSave={onSubmit}
      fixSize="small"
    >
      {loading && <Loader />}
      <group data-space="15" data-gap="10" data-direction="column">
      <Select
        autoComplete=""
        label="Kibosh Device"
        labelPosition="left"
        dataLength="320"
        defaultValue={currentKiboshDeviceReference}
        onChange={(e: any) => setCurrentKiboshDeviceReference(e)}
        loading={loading}
        allowSearch={true}
        options={
          accountKiboshDevices?.map((item) => {
            return {
              text: `${item.kiboshDeviceName!} (${item.kiboshDeviceReference!})`,
              value: item.kiboshDeviceReference!,
            };
          }) || []
        }
        button={
          <Button
            material
            icon="refresh"
            onClick={getAccountKiboshDevicesAsync}
          />
        }
      />
      </group>
    </Popup>
  );
}
